import * as React from 'react';
import type { CalendarConnectionStatus } from '../services/meetingsApi';
import { meetingsApi } from '../services/meetingsApi';

interface UseCalendarConnectionState {
  status: CalendarConnectionStatus | null;
  loading: boolean;
  connecting: boolean;
  error: string | null;
}

/**
 * Manages Google Calendar connection state for the current user.
 *
 * Fetches `/api/integration/google-calendar/status` on mount and after OAuth.
 * `connect()` opens the OAuth consent screen in a popup, waits for the
 * BroadcastChannel result from OAuthResultPage, then refreshes status.
 */
export function useCalendarConnection() {
  const [state, setState] = React.useState<UseCalendarConnectionState>({
    status: null,
    loading: true,
    connecting: false,
    error: null,
  });

  const fetchStatus = React.useCallback(async () => {
    try {
      const status = await meetingsApi.getConnectionStatus();
      setState((prev) => ({ ...prev, status, loading: false, error: null }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load connection status',
      }));
    }
  }, []);

  React.useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  const connect = React.useCallback(async () => {
    setState((prev) => ({ ...prev, connecting: true, error: null }));

    // Open a blank popup synchronously so the browser treats it as a
    // user-gesture-initiated window (avoids popup blockers).
    // We navigate it to the real OAuth URL once we have it.
    const popup = window.open('about:blank', 'google-calendar-oauth', 'width=600,height=700');

    try {
      const { url } = await meetingsApi.getConnectUrl();

      if (!popup || popup.closed) {
        // Popup was blocked or closed before we got the URL - treat as cancel
        setState((prev) => ({ ...prev, connecting: false }));
        return;
      }

      // Navigate the already-open popup to the OAuth URL
      popup.location.href = url;

      await new Promise<void>((resolve, reject) => {
        const channel = new BroadcastChannel('google-calendar-oauth');

        const cleanup = () => {
          channel.close();
        };

        channel.onmessage = (event: MessageEvent<{ type: string; error?: string }>) => {
          cleanup();
          if (event.data.type === 'GOOGLE_CALENDAR_OAUTH_SUCCESS') {
            resolve();
          } else {
            reject(new Error(event.data.error ?? 'OAuth failed'));
          }
        };

        // Detect if the popup was closed without completing OAuth.
        const pollClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(pollClosed);
            cleanup();
            reject(new Error('OAuth popup was closed'));
          }
        }, 500);
      });

      // Reload connection status after successful connect
      await fetchStatus();
      setState((prev) => ({ ...prev, connecting: false }));
    } catch (err) {
      popup?.close();
      const message = err instanceof Error ? err.message : 'Connection failed';
      // Ignore "popup was closed" - the user cancelled deliberately, not an error
      const isUserCancel = message === 'OAuth popup was closed';
      setState((prev) => ({
        ...prev,
        connecting: false,
        error: isUserCancel ? null : message,
      }));
    }
  }, [fetchStatus]);

  return { ...state, connect, refetch: fetchStatus };
}
